import React, { useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../api';
import { useAsync } from '../../hooks/useAsync';
import type { Company, FileItem, FolderNode } from '../../types';
import { useToast } from '../../components/ToastHost';
import { canPreviewOnline, fileTypeLabel } from '../../utils/fileType';

type AdminTreeResp = {
  folders: FolderNode[];
  files: FileItem[];
  companies: Array<Pick<Company, 'id' | 'name' | 'dataRoomAccessMode'>>;
  folderAccessByCompany: Record<string, string[]>;
};

function buildChildren(folders: FolderNode[]) {
  const map = new Map<string | null, FolderNode[]>();
  folders.forEach((f) => {
    const k = f.parentId || null;
    map.set(k, [...(map.get(k) || []), f]);
  });
  map.forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
  return map;
}

export default function AdminDataRoom() {
  const { data, loading, error, setData } = useAsync<AdminTreeResp>(() => apiGet('/api/admin/data-room/tree'), []);
  const toast = useToast();
  const [newFolderName, setNewFolderName] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const folders = data?.folders || [];
  const files = data?.files || [];
  const children = useMemo(() => buildChildren(folders), [folders]);

  const filesByFolder = useMemo(() => {
    const map = new Map<string, FileItem[]>();
    files.forEach((fi) => map.set(fi.folderId, [...(map.get(fi.folderId) || []), fi]));
    map.forEach((arr) => arr.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    return map;
  }, [files]);

  const refresh = async () => setData(await apiGet('/api/admin/data-room/tree'));

  const createFolder = async () => {
    setCreatingFolder(true);
    try {
      await apiPost('/api/admin/folders', { name: newFolderName, parentId });
      setNewFolderName('');
      await refresh();
      toast.success('Folder created.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not create folder.');
      throw e;
    } finally {
      setCreatingFolder(false);
    }
  };

  const uploadMany = async (filesToUpload: File[], folderId: string) => {
    if (!filesToUpload.length) return;
    setUploading(true);
    setUploadingCount(filesToUpload.length);
    let okCount = 0;
    let failCount = 0;
    try {
      for (const file of filesToUpload) {
        try {
          const fd = new FormData();
          fd.set('folderId', folderId);
          fd.set('file', file);
          const resp = await fetch('/api/admin/files/upload', { method: 'POST', body: fd, credentials: 'include' });
          if (!resp.ok) {
            const t = await resp.text();
            throw new Error(t || 'upload failed');
          }
          okCount++;
        } catch {
          failCount++;
        } finally {
          setUploadingCount((n) => Math.max(0, n - 1));
        }
      }
      await refresh();
      if (okCount) toast.success(`Uploaded ${okCount} file${okCount === 1 ? '' : 's'}.`);
      if (failCount) toast.error(`${failCount} file${failCount === 1 ? '' : 's'} failed to upload.`);
    } finally {
      setUploading(false);
      setUploadingCount(0);
    }
  };

  const renderFolder = (pid: string | null) => {
    const kids = children.get(pid) || [];
    if (!kids.length) return null;
    return (
      <ul>
        {kids.map((f) => (
          <li key={f.id}>
            <div
              className={`folder-tree__item ${selectedFolderId === f.id ? 'is-selected' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedFolderId(f.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelectedFolderId(f.id);
              }}
            >
              <span className="folder-tree__dot" aria-hidden="true" />
              <span style={{ fontWeight: selectedFolderId === f.id ? 900 : 700 }}>{f.name}</span>
            </div>
            {renderFolder(f.id)}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) return <div className="card">Loading…</div>;
  if (error || !data) return <div className="card">Error: {error || 'unknown'} (check Basic Auth)</div>;

  const folderId = selectedFolderId || folders.find((f) => f.parentId == null)?.id || null;
  const folderFiles = folderId ? filesByFolder.get(folderId) || [] : [];
  const selectedFolderName = folderId ? folders.find((f) => f.id === folderId)?.name || null : null;

  return (
    <div className="grid" style={{ gridTemplateColumns: '360px 1fr', gap: 14 }}>
      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Folders</div>
        <div className="folder-tree">{renderFolder(null)}</div>
        <div className="hr" />
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          Create folder
        </div>
        <input className="input" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" />
        <div style={{ height: 8 }} />
        <select className="select" value={parentId || ''} onChange={(e) => setParentId(e.target.value || null)}>
          <option value="">(root)</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <div style={{ height: 8 }} />
        <button className="btn primary" disabled={!newFolderName.trim() || creatingFolder} onClick={createFolder}>
          {creatingFolder ? 'Creating…' : 'Create'}
        </button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>Files</div>
          {folderId ? (
            <label className="btn">
              {uploading ? `Uploading… (${uploadingCount})` : 'Upload files'}
              <input
                type="file"
                multiple
                hidden
                disabled={uploading}
                onChange={(e) => {
                  const fs = Array.from(e.target.files || []);
                  if (!fs.length || !folderId) return;
                  uploadMany(fs, folderId).catch(() => {});
                  e.currentTarget.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        <div className="hr" />
        {!folderId ? (
          <div className="muted">Create a folder to upload files.</div>
        ) : (
          <>
            {selectedFolderName ? (
              <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                Folder: <span className="pill">{selectedFolderName}</span>
              </div>
            ) : null}
            <div className={`dropzone ${isDragging ? 'is-dragging' : ''}`}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                if (!folderId) return;
                const fs = Array.from(e.dataTransfer.files || []);
                if (!fs.length) return;
                uploadMany(fs, folderId).catch(() => {});
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <div style={{ fontWeight: 850 }}>Drop files here</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Uploads to the selected folder
                </div>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                You can drag & drop multiple files at once.
              </div>
            </div>
            <div style={{ height: 12 }} />
            {!folderFiles.length ? (
              <div className="muted">No files in this folder.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th className="muted">Size</th>
                    <th className="muted">Uploaded</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {folderFiles.map((f) => (
                    <tr key={f.id}>
                      <td>{f.name}</td>
                      <td className="muted">{fileTypeLabel(f.name, f.mimeType)}</td>
                      <td className="muted">{f.sizeBytes ? `${Math.round(f.sizeBytes / 1024)} KB` : '—'}</td>
                      <td className="muted">{f.createdAt ? f.createdAt.slice(0, 10) : '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canPreviewOnline(f.mimeType) ? (
                          <a className="btn" href={`/api/admin/files/${f.id}/view`} target="_blank" rel="noopener">
                            Preview online
                          </a>
                        ) : null}{' '}
                        <a className="btn" href={`/api/admin/files/${f.id}/download`} target="_blank" rel="noopener">
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
